import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';

const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'Real Brasileiro (BRL)' },
  { value: 'USD', label: 'Dólar Americano (USD)' },
];

const DUE_DATE_TYPES = [
  { value: 'fixed_day', label: 'Fixar dia do mês (1 a 31)' },
  { value: 'days_after_invoice', label: 'Número de dias após faturar' },
];

const Step4Details = () => {
  const navigate = useNavigate();
  const { state, setSubscriptionDetails } = useSubscriptionWizard();
  
  const [currencyCode, setCurrencyCode] = useState(state.currencyCode);
  const [poNumber, setPoNumber] = useState(state.poNumber);
  const [paymentSourceId, setPaymentSourceId] = useState(state.paymentSourceId);
  
  // Novos campos para vencimento
  const [dueDateType, setDueDateType] = useState(state.dueDateType || 'fixed_day');
  const [dueDateDay, setDueDateDay] = useState(state.dueDateDay?.toString() || '5');
  const [dueDateNextMonth, setDueDateNextMonth] = useState(state.dueDateNextMonth || false);

  useEffect(() => {
    if (!state.selectedCustomer) {
      // Redirect back if previous steps are not completed
      navigate('../step1', { relative: 'path' });
      return;
    }
  }, [state.selectedCustomer, navigate]);

  const handleNext = () => {
    // Validate and save details
    if (!currencyCode) {
      toast.error('Por favor, selecione a moeda');
      return;
    }

    if (!paymentSourceId) {
      toast.error('Por favor, selecione a forma de pagamento');
      return;
    }
    
    // Validação do dia do mês (entre 1 e 31)
    if (dueDateType === 'fixed_day') {
      const day = parseInt(dueDateDay);
      if (isNaN(day) || day < 1 || day > 31) {
        toast.error('O dia do mês deve ser um número entre 1 e 31');
        return;
      }
    }

    // Use current date as the default startDate
    const today = new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD
    
    // Save details to context
    setSubscriptionDetails({
      startDate: today, // Set to today's date
      currencyCode,
      poNumber,
      paymentSourceId,
      netTermDays: 0, // Definido como zero padrão
      dueDateType,
      dueDateDay: parseInt(dueDateDay, 10),
      dueDateNextMonth,
    });

    navigate('../step5', { relative: 'path' });
  };

  return (
    <SubscriptionWizardLayout
      currentStep={4}
      totalSteps={5}
      title="Nova Assinatura - Detalhes da Assinatura"
      description="Configure os detalhes da assinatura"
      onNext={handleNext}
      onPrevious={() => navigate('../step3', { relative: 'path' })}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label htmlFor="currencyCode" className="required">Moeda</Label>
            <Select 
              value={currencyCode} 
              onValueChange={setCurrencyCode}
            >
              <SelectTrigger id="currencyCode">
                <SelectValue placeholder="Selecione a moeda" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((currency) => (
                  <SelectItem key={currency.value} value={currency.value}>
                    {currency.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Moeda utilizada para cobranças
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="poNumber">Número do Pedido (PO)</Label>
            <Input
              id="poNumber"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="Ex: PO-12345"
            />
            <p className="text-xs text-muted-foreground">
              Número de ordem de compra do cliente (opcional)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentSourceId" className="required">
              Forma de Pagamento <span className="text-red-500">*</span>
            </Label>
            <Select
              value={paymentSourceId}
              onValueChange={(value) => setPaymentSourceId(value)}
              required
            >
              <SelectTrigger 
                id="paymentSourceId"
                className={!paymentSourceId ? "border-red-300 focus:ring-red-500" : ""}
              >
                <SelectValue placeholder="Selecione a forma de pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="boleto">Boleto Bancário</SelectItem>
                <SelectItem value="transferencia">Transferência</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Método de pagamento utilizado para esta assinatura (obrigatório)
            </p>
          </div>
          
          {/* Campos de vencimento */}
          <div className="space-y-2">
            <Label htmlFor="dueDateType">Tipo de Vencimento</Label>
            <Select 
              value={dueDateType} 
              onValueChange={setDueDateType}
            >
              <SelectTrigger id="dueDateType">
                <SelectValue placeholder="Selecione o tipo de vencimento" />
              </SelectTrigger>
              <SelectContent>
                {DUE_DATE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="dueDateDay">
              {dueDateType === 'fixed_day' ? 'Dia do Mês' : 'Número de Dias'}
            </Label>
            <Input
              id="dueDateDay"
              type="number"
              min="1"
              max={dueDateType === 'fixed_day' ? "31" : "90"}
              value={dueDateDay}
              onChange={(e) => setDueDateDay(e.target.value)}
              placeholder={dueDateType === 'fixed_day' ? "Ex: 5" : "Ex: 10"}
            />
            {dueDateType === 'days_after_invoice' && (
              <p className="text-xs text-muted-foreground">
                Dias para pagamento após emissão da fatura
              </p>
            )}
          </div>
          
          {dueDateType === 'fixed_day' && (
            <div className="flex items-center justify-between space-x-2 pt-4">
              <div className="space-y-0.5">
                <Label htmlFor="dueDateNextMonth" className="font-extrabold">
                  Do próximo mês
                </Label>
                <p className="text-xs text-muted-foreground">
                  Vencimento será no mês seguinte à emissão
                </p>
              </div>
              <Switch
                id="dueDateNextMonth"
                checked={dueDateNextMonth}
                onCheckedChange={setDueDateNextMonth}
              />
            </div>
          )}
        </div>
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step4Details; 